// Shared primitives for the Analytics workbench tab: the object-type color
// map + legend blurbs (spec §0.4), rail definition (§3.1), type chips,
// eyebrow labels and small helpers. Colors for the type chips/dots are the
// spec's object-type identity colors (they ARE the object's identity, same
// in both themes); everything else uses Twasta's --color-* variables.
import type { CSSProperties, ReactNode } from 'react'

export type AnalyticsSection =
  | 'overview'
  | 'connections'
  | 'areas'
  | 'schema'
  | 'visuals'
  | 'dashboards'
  | 'reports'
  | 'processes'
  | 'smartpages'
  | 'foresight'
  | 'build'

/** Object-type color map — used by legend, chips, rail dots (spec §0.4). */
export const TYPE_COLORS: Record<string, string> = {
  M: '#a371f7',
  V: '#4c8dff',
  D: '#3fb950',
  A: '#f0883e',
  R: '#db61a2',
  G: '#7ee787',
  S: '#56d4dd',
  F: '#d29922',
  '-': '#6e7681',
}

export const OBJECT_TYPE_LEGEND: { code: string; name: string; blurb: string }[] = [
  { code: 'A', name: 'Business Area', blurb: 'A topic the AI Assistant can answer on' },
  { code: 'M', name: 'Data Model', blurb: 'Which tables & joins your charts can use' },
  { code: 'V', name: 'Visual', blurb: 'One chart, table or KPI' },
  { code: 'D', name: 'Dashboard', blurb: 'A grid of visuals with shared filters' },
  { code: 'R', name: 'Report', blurb: 'A pixel-perfect printable (Jasper) report' },
  { code: 'G', name: 'General Process', blurb: 'A batch run: source SQL → calculations → posted rows' },
  { code: 'S', name: 'Smart Page', blurb: 'A page that can embed visuals & dashboards' },
  { code: 'F', name: 'Foresight', blurb: 'A forecasting or prediction (ML) experiment' },
  { code: '-', name: 'Folder', blurb: 'Groups items in the navigation menu' },
]

export const RAIL_ITEMS: { key: AnalyticsSection; label: string; dot: string }[] = [
  { key: 'overview', label: 'Overview', dot: '#4c8dff' },
  { key: 'connections', label: 'Connections', dot: '#a371f7' },
  { key: 'areas', label: 'Business Areas', dot: '#f0883e' },
  { key: 'schema', label: 'Schema Design', dot: '#3fb950' },
  { key: 'visuals', label: 'Visuals', dot: '#58a6ff' },
  { key: 'dashboards', label: 'Dashboards', dot: '#56d4dd' },
  { key: 'reports', label: 'Reports', dot: '#db61a2' },
  { key: 'processes', label: 'Processes', dot: '#7ee787' },
  { key: 'smartpages', label: 'Smart Pages', dot: '#e3b341' },
  { key: 'foresight', label: 'Foresight', dot: '#d29922' },
]

/** Square colored letter chip (sizes 16–30 per spec §0.3). */
export function TypeChip({ code, size = 18, color }: { code: string; size?: number; color?: string }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: Math.max(4, Math.round(size / 4.5)),
        background: color || TYPE_COLORS[code] || '#6e7681',
        color: '#fff',
        fontWeight: 700,
        fontSize: Math.max(9, Math.round(size * 0.55)),
        lineHeight: 1,
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {code}
    </span>
  )
}

/** Section eyebrow label (spec §0.3): 11.5px / 700 / uppercase / faint. */
export const eyebrowStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-text-faint)',
}

/** Card / panel (spec §0.3): surface bg + border, radius 12 — visibly
    stepped from the page background (--color-bg). */
export const cardStyle: CSSProperties = {
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 12,
}

/** USED / UNUSED (and generic) micro-badge — prototype treatment (§0.3):
    weak tint background + strong text color (e.g. success-weak / success). */
export function MicroBadge({
  text, tone = 'muted',
}: {
  text: string
  tone?: 'success' | 'muted' | 'warn' | 'accent' | 'error'
}) {
  const tones: Record<string, { bg: string; fg: string }> = {
    success: { bg: 'var(--color-success-weak)', fg: 'var(--color-success)' },
    muted: { bg: 'var(--color-surface-2)', fg: 'var(--color-text-secondary)' },
    warn: { bg: 'var(--color-warning-weak)', fg: 'var(--color-warning)' },
    accent: { bg: 'var(--color-accent-weak)', fg: 'var(--color-accent)' },
    error: { bg: 'var(--color-error-weak)', fg: 'var(--color-error)' },
  }
  const t = tones[tone] || tones.muted
  return (
    <span
      style={{
        fontSize: 13,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 7,
        color: t.fg,
        background: t.bg,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {text}
    </span>
  )
}

/** Centered empty-state coach block (icon tile + title + helper). */
export function EmptyStateCoach({
  icon, title, body, action,
}: {
  icon: ReactNode
  title: string
  body: ReactNode
  action?: ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '56px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-secondary)',
        }}
      >
        {icon}
      </div>
      <div style={{ fontWeight: 600, fontSize: 17 }}>{title}</div>
      <div style={{ fontSize: 15.5, color: 'var(--color-text-secondary)', maxWidth: 440, lineHeight: 1.55 }}>{body}</div>
      {action}
    </div>
  )
}

/** snake_case / free text → obj_name-safe slug. */
export function slugify(name: string): string {
  return (name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'new_object'
}

/** Compact error line with a retry button — every fetch surface uses it. */
export function InlineError({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        border: '1px solid var(--color-error)',
        background: 'var(--color-error-weak)',
        color: 'var(--color-error)',
        fontSize: 15,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{message}</span>
      {onRetry && (
        <button className="btn btn-ghost btn-sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  )
}

export function errMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  // Structured details (e.g. the delete-dependency 409 body) carry a message.
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
